import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface UseCanonicalUrlOptions {
  baseUrl?: string;
  path?: string;
}

export const useCanonicalUrl = (options: UseCanonicalUrlOptions = {}) => {
  const location = useLocation();
  const { baseUrl = 'https://www.getwishful.com', path } = options;

  useEffect(() => {
    // Determine the canonical URL
    const canonicalPath = path || location.pathname;
    const canonicalUrl = `${baseUrl}${canonicalPath}`;

    // Remove existing canonical link if it exists
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Create and add new canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);

    // Also update the page title if it's not already set
    if (!document.title || document.title === 'Wishful - Free Wishlist & Gift Planning Tools') {
      document.title = getPageTitle(canonicalPath);
    }

    // Cleanup function to remove canonical link when component unmounts
    return () => {
      if (canonicalLink.parentNode) {
        canonicalLink.parentNode.removeChild(canonicalLink);
      }
    };
  }, [location.pathname, baseUrl, path]);

  return `${baseUrl}${path || location.pathname}`;
};

// Helper function to generate page titles based on path
const getPageTitle = (path: string): string => {
  const titleMap: Record<string, string> = {
    '/': 'Wishful - Free Wishlist & Gift Planning Tools',
    '/dashboard': 'Dashboard - Wishful',
    '/account/settings': 'Account Settings - Wishful',
    '/wishing-well': 'Wishing Well - Wishful',
    '/holiday-calendar-2025': 'Holiday Calendar 2025 - Wishful',
    '/secret-santa-generator': 'Secret Santa Generator - Wishful',
    '/gift-guides': 'Gift Guides - Wishful',
    '/price-tracker': 'Price Tracker - Wishful',
    '/blog': 'Blog - Wishful',
    '/onboarding': 'Get Started - Wishful',
  };

  // Handle dynamic routes
  if (path.startsWith('/wishlist/')) {
    return 'Wishlist - Wishful';
  }
  if (path.startsWith('/blog/')) {
    return 'Blog Post - Wishful';
  }

  return titleMap[path] || 'Wishful - Free Wishlist & Gift Planning Tools';
};
